import React, { useEffect } from 'react';
import { Row, Col, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import _ from 'lodash';

import { NumberOnlyField, SelectField } from 'src/components/form';
import {
  MACHINING_OPTIONS,
  CORNER_OPTIONS,
} from 'src/constants/quotation/optionSelect';
import { FormValue } from '../useForm';
import {
  getVisiableMachining,
  getVisiableCorner,
  getVisiableChamfer,
} from 'src/utils/quotation';
import { MACHINING_TOLERANCE } from 'src/constants/app';
import { useToleranceGroupFilter } from 'src/hooks/useToleranceGroupFilter';

type Props = {
  machiningMethod: string;
  formInfo: UseFormReturn<FormValue, any>;
  toleranceOptions: Response.ToleranceGroupOptions;
  product: string;
  dataReEstimate: any;
  isResetMachiningTolerance: boolean;
  showMachiningTolerance: boolean;
  setShowMachiningTolerance: (item: boolean) => void;
};

const SizeInput: React.FC<Props> = ({
  machiningMethod,
  formInfo,
  toleranceOptions,
  dataReEstimate,
  product,
  isResetMachiningTolerance,
  showMachiningTolerance,
  setShowMachiningTolerance,
}) => {
  const { control, getValues, setValue, watch } = formInfo;
  const { t } = useTranslation();

  // Watch tolerance fields for group locking
  const watchedThickness = watch('machining_thickness');
  const watchedWidth = watch('machining_width');
  const watchedLength = watch('machining_length');
  const watchedOutside = watch('machining_outside_diameter');

  const checkVisiableMachiningInitial = (tolerance) =>
    getVisiableMachining(getValues('shape'), machiningMethod, tolerance);

  // Use custom hook for tolerance group filtering
  const { filteredOptions: machiningOptions } = useToleranceGroupFilter({
    product,
    toleranceOptions,
    watchedValues: {
      thickness: watchedThickness,
      width: watchedWidth,
      length: watchedLength,
      outside: watchedOutside,
    },
  });

  const machiningThicknessOptions = machiningOptions.thickness;
  const machiningWidthOptions = machiningOptions.width;
  const machiningLengthOptions = machiningOptions.length;
  const machiningOutsideOptions = machiningOptions.outside;

  const isVisiableMachining = (tolerance, machiningOptions) => {
    //before call API tolerance : checkVisiableMachiningInitial
    //after call API tolerance : check !_.isEmpty(machiningOptions)
    return (
      checkVisiableMachiningInitial(tolerance) &&
      (toleranceOptions?.apiCalled ? !_.isEmpty(machiningOptions) : true)
    );
  };

  const isVisiableMachiningThickness = isVisiableMachining(
    MACHINING_TOLERANCE.PLATE_THICKNESS,
    machiningThicknessOptions,
  );
  const isVisiableMachiningWidth = isVisiableMachining(
    MACHINING_TOLERANCE.WIDTH,
    machiningWidthOptions,
  );
  const isVisiableMachiningLength = isVisiableMachining(
    MACHINING_TOLERANCE.LENGTH,
    machiningLengthOptions,
  );
  const isVisiableMachiningOutside = isVisiableMachining(
    MACHINING_TOLERANCE.OUTSIDE_DIAMETER,
    machiningOutsideOptions,
  );
  const isVisiableMachiningInside = checkVisiableMachiningInitial(
    MACHINING_TOLERANCE.INSIDE_DIAMETER,
  );

  useEffect(() => {
    if (isResetMachiningTolerance) return;
    if (!dataReEstimate) {
      isVisiableMachiningThickness &&
        setValue(
          'machining_thickness',
          machiningThicknessOptions?.[0]?.value || '',
        );
      isVisiableMachiningWidth &&
        setValue('machining_width', machiningWidthOptions?.[0]?.value || '');
      isVisiableMachiningLength &&
        setValue('machining_length', machiningLengthOptions?.[0]?.value || '');
      isVisiableMachiningOutside &&
        setValue(
          'machining_outside_diameter',
          machiningOutsideOptions?.[0]?.value || '',
        );
      isVisiableMachiningInside &&
        setValue('machining_inside_diameter', MACHINING_OPTIONS[0].value);
    } else {
      isVisiableMachiningThickness &&
        setValue('machining_thickness', dataReEstimate?.optc001 || '');
      isVisiableMachiningWidth &&
        setValue('machining_width', dataReEstimate?.optc002 || '');
      isVisiableMachiningLength &&
        setValue('machining_length', dataReEstimate?.optc003 || '');
      isVisiableMachiningOutside &&
        setValue('machining_outside_diameter', dataReEstimate?.optc004 || '');
    }
  }, [dataReEstimate, isResetMachiningTolerance]);

  const clearMachiningToleranceValue = () => {
    !isVisiableMachiningThickness && setValue('machining_thickness', '');
    !isVisiableMachiningWidth && setValue('machining_width', '');
    !isVisiableMachiningLength && setValue('machining_length', '');
    !isVisiableMachiningOutside && setValue('machining_outside_diameter', '');
    !isVisiableMachiningInside && setValue('machining_inside_diameter', '');

    if (!getVisiableCorner(getValues('shape'), machiningMethod)) {
      setValue('corner_option_a', '');
      setValue('corner_option_b', '');
      setValue('corner_option_c', '');
      setValue('corner_option_d', '');
    }

    !getVisiableChamfer(getValues('shape'), machiningMethod, 0) &&
      setValue('options_size_unspecified', '');

    !getVisiableChamfer(getValues('shape'), machiningMethod, 1) &&
      setValue('options_size_c', '');
  };

  useEffect(() => {
    setShowMachiningTolerance(
      _.some([
        isVisiableMachiningThickness,
        isVisiableMachiningWidth,
        isVisiableMachiningLength,
        isVisiableMachiningOutside,
        isVisiableMachiningInside,
      ]),
    );
  }, [
    isVisiableMachiningThickness,
    isVisiableMachiningWidth,
    isVisiableMachiningLength,
    isVisiableMachiningOutside,
    isVisiableMachiningInside,
  ]);

  useEffect(() => {
    clearMachiningToleranceValue();
  }, [machiningMethod]);

  return (
    <Row className="container_machining_tolerance mb-10">
      <Col xs={24} md={24} xl={24} className="options_size">
        {showMachiningTolerance && (
          <Row>
            <Col xs={24} sm={4} md={4} xl={4} className="mt-5">
              <Typography.Text className="form_label_no_required">
                {t('pricing_page.machining_tolerance.name')}
              </Typography.Text>
            </Col>
            <Col
              xs={24}
              sm={19}
              md={19}
              lg={19}
              xl={19}
              className="options_size_input"
            >
              <Row gutter={[16, 0]}>
                <Col xs={24} md={12} xl={12} className="p-0">
                  {isVisiableMachiningThickness && (
                    <Col xs={24} md={24} xl={24}>
                      <SelectField<FormValue>
                        controller={{
                          control,
                          name: 'machining_thickness',
                        }}
                        selectProps={{
                          placeholder: t(
                            'pricing_page.machining_tolerance.plate_thickness',
                          ),
                          options: machiningThicknessOptions,
                          allowClear: true,
                        }}
                        label={t(
                          'pricing_page.machining_tolerance.plate_thickness',
                        )}
                        className="machining_select w-full mb-0"
                      />
                    </Col>
                  )}
                  {isVisiableMachiningWidth && (
                    <Col xs={24} md={24} xl={24}>
                      <SelectField<FormValue>
                        controller={{ control, name: 'machining_width' }}
                        selectProps={{
                          placeholder: t(
                            'pricing_page.machining_tolerance.width',
                          ),
                          options: machiningWidthOptions,
                          allowClear: true,
                        }}
                        label={t('pricing_page.machining_tolerance.width')}
                        className="machining_select mb-0"
                      />
                    </Col>
                  )}
                  {isVisiableMachiningLength && (
                    <Col xs={24} md={24} xl={24}>
                      <SelectField<FormValue>
                        controller={{ control, name: 'machining_length' }}
                        selectProps={{
                          placeholder: t(
                            'pricing_page.machining_tolerance.length',
                          ),
                          options: machiningLengthOptions,
                          allowClear: true,
                        }}
                        label={t('pricing_page.machining_tolerance.length')}
                        className="machining_select mb-0"
                      />
                    </Col>
                  )}
                  {isVisiableMachiningOutside && (
                    <Col xs={24} md={24} xl={24}>
                      <SelectField<FormValue>
                        controller={{
                          control,
                          name: 'machining_outside_diameter',
                        }}
                        selectProps={{
                          placeholder: t(
                            'pricing_page.machining_tolerance.outside_diameter',
                          ),
                          options: machiningOutsideOptions,
                          allowClear: true,
                        }}
                        label={t(
                          'pricing_page.machining_tolerance.outside_diameter',
                        )}
                      />
                    </Col>
                  )}
                  {isVisiableMachiningInside && (
                    <Col xs={24} md={24} xl={24}>
                      <SelectField<FormValue>
                        controller={{
                          control,
                          name: 'machining_inside_diameter',
                        }}
                        selectProps={{
                          placeholder: t(
                            'pricing_page.machining_tolerance.inside_diameter',
                          ),
                          options: MACHINING_OPTIONS,
                          allowClear: true,
                        }}
                        label={t(
                          'pricing_page.machining_tolerance.inside_diameter',
                        )}
                      />
                    </Col>
                  )}
                </Col>
                <Col xs={24} md={12} xl={12}>
                  {getVisiableCorner(getValues('shape'), machiningMethod) && (
                    <Row className="container_box_corner">
                      <Col sm={24} md={24} xl={24} className="form_typo">
                        <Typography.Text className="form_label_no_required custom_form_label_no_required">
                          {t('pricing_page.corner_handling_options.name')}
                        </Typography.Text>
                      </Col>
                      <Col span={24} className="box_corner custombox_corner">
                        <Row
                          justify="space-between"
                          className="box_corner_container"
                          align="top"
                        >
                          <Col xs={6} md={6} xl={6}>
                            <NumberOnlyField<FormValue>
                              controller={{ control, name: 'corner_option_a' }}
                              inputProps={{
                                maxLength: 4,
                              }}
                              labelCol={{ span: 4 }}
                              wrapperCol={{ span: 20 }}
                              label={t(
                                'pricing_page.corner_handling_options.A',
                              )}
                              className="box_corner_first_input "
                            />
                            <NumberOnlyField<FormValue>
                              controller={{ control, name: 'corner_option_b' }}
                              inputProps={{
                                maxLength: 4,
                              }}
                              labelCol={{ span: 4 }}
                              wrapperCol={{ span: 20 }}
                              label={t(
                                'pricing_page.corner_handling_options.B',
                              )}
                            />
                          </Col>
                          <Col
                            xs={10}
                            md={10}
                            xl={10}
                            className="box_corner_box"
                          >
                            <Row>
                              <Col className="box_corner_box_x">
                                <Typography.Text className="after_label_text">
                                  {t(
                                    'pricing_page.corner_handling_options.width',
                                  )}
                                </Typography.Text>
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={3} md={2} className="box_corner_box_y">
                                <Typography.Text className="after_label_text">
                                  {t(
                                    'pricing_page.corner_handling_options.height',
                                  )}
                                </Typography.Text>
                              </Col>
                              <Col xs={21} md={22}>
                                <div className="box_corner_preview"></div>
                              </Col>
                            </Row>
                          </Col>
                          <Col xs={6} md={6} xl={6}>
                            <NumberOnlyField<FormValue>
                              controller={{ control, name: 'corner_option_c' }}
                              inputProps={{
                                maxLength: 4,
                              }}
                              labelCol={{ span: 4 }}
                              wrapperCol={{ span: 20 }}
                              label={t(
                                'pricing_page.corner_handling_options.C',
                              )}
                              className="box_corner_first_input"
                            />
                            <NumberOnlyField<FormValue>
                              controller={{ control, name: 'corner_option_d' }}
                              inputProps={{
                                maxLength: 4,
                              }}
                              labelCol={{ span: 4 }}
                              wrapperCol={{ span: 20 }}
                              label={t(
                                'pricing_page.corner_handling_options.D',
                              )}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        )}
        {getVisiableChamfer(getValues('shape'), machiningMethod, 0) &&
          getVisiableChamfer(getValues('shape'), machiningMethod, 1) && (
            <Row className="mt-15">
              <Col xs={24} sm={4} md={4} xl={4} lg={4} className="-mt-40">
                <Typography.Text className="form_label_no_required">
                  {t('pricing_page.around_chamfer.name')}
                </Typography.Text>
              </Col>
              <Col
                xs={24}
                sm={19}
                md={19}
                lg={19}
                xl={19}
                className="options_size_input"
              >
                <Row
                  align="top"
                  className="option_c custom_option_c"
                  gutter={[16, 0]}
                >
                  {getVisiableChamfer(
                    getValues('shape'),
                    machiningMethod,
                    0,
                  ) && (
                    <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                      <SelectField<FormValue>
                        controller={{
                          control,
                          name: 'options_size_unspecified',
                        }}
                        selectProps={{
                          placeholder: t(
                            'pricing_page.around_chamfer.unspecified',
                          ),
                          allowClear: true,
                          options: CORNER_OPTIONS(t),
                          defaultValue: CORNER_OPTIONS(t)[0].value,
                        }}
                      />
                    </Col>
                  )}
                  {getVisiableChamfer(
                    getValues('shape'),
                    machiningMethod,
                    1,
                  ) && (
                    <Col
                      xs={24}
                      sm={12}
                      md={6}
                      xl={6}
                      lg={6}
                      className="options_size_val"
                    >
                      <NumberOnlyField<FormValue>
                        controller={{ control, name: 'options_size_c' }}
                        inputProps={{ maxLength: 4 }}
                        labelCol={{ span: 3 }}
                        className="w-full"
                        label={t('pricing_page.around_chamfer.C')}
                      />
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
          )}
      </Col>
    </Row>
  );
};

export default SizeInput;
